/*
  spx-catalog-scroll.js — infinite scroll v3 for the sp-catalog grid
  (CATALOG_REBUILD_PLAN §2 assets / §3 item 3 / Gate 3). Vanilla ES5, IIFE, no jQuery.

  RESPONSIBILITIES
  - IntersectionObserver sentinel before the server pagination (bottom backstop), plus a
    scroll-progress prefetch: a passive, rAF-throttled scroll/resize listener that fires
    the next fetch once the viewport passes `prefetchRatio` (default 0.7, data-sp-prefetch-
    ratio) of the LOADED grid — so a fast flick can't outrun the network round-trip. This
    is NOT the legacy 1.5s interval poller (that stays gone, plan §3.3): it's event-driven
    and self-removes at end-of-catalog / Load-more handoff.
  - Next page fetched via the Section Rendering API as
      {location.pathname}?{full location.search minus page/section_id}&page=N&section_id={sectionId}
    so EVERY active filter param rides along (fixes Bug 2 at the root). The page
    number comes from the server-rendered next link — never from a blind counter.
  - Connection-aware throttle (navigator.connection):
      saveData or effectiveType slow-2g/2g  -> 0 auto pages (manual "Load more" only)
      effectiveType 3g                      -> 1 auto page, then "Load more"
      otherwise                             -> data-sp-auto-pages (grid section setting),
                                               default DEFAULTS.autoPages
  - Accurate total count: after each append, re-reads data-sp-total off the fetched
    section root (fallback: the inline `window._spCollectionTotal = N` script, regex),
    updates window._spCollectionTotal (theme-decode §4 contract) and patches the
    first number inside any [data-sp-count] element without touching its wording.
  - History-SAFE: we deliberately NEVER write `?page=N` to the URL. Doing so broke the
    browser Back button — after visiting a product and hitting Back, the browser restored
    the polluted `?page=N`, Shopify server-rendered ONLY that page's slice, and every
    earlier product vanished from the grid. Instead, on load we DETECT a stray `?page=N`
    (old bookmark / legacy link) and reset to the clean page-1 URL so the whole collection
    loads. (Filter pushState still belongs to spx-catalog.js; we touch neither.)
  - Stops cleanly at the end (no next link -> teardown, pagination stays hidden —
    everything is already on the page). On fetch ERROR it degrades to full
    navigation: unhides the server pagination and never caches the failure.
  - Idempotent re-init exported as window.spIfsInit (compat name — spx-catalog.js
    calls it after every section swap; also self-heals on 'collection:reloaded').

  DOM CONTRACT (sections/sp-catalog-grid.liquid)              -- TODO-VERIFY --
    root        #sp-catalog-grid  (fallback [data-sp-catalog-grid])
                  optional attrs: data-sp-auto-pages, data-sp-total
    cards       [data-sp-cards]   (fallbacks .sp-catalog-grid__cards, .sp-card-grid,
                                   .product-grid)
    pagination  [data-sp-pagination] (fallbacks .sp-catalog-pagination, .pagination)
    next link   a[rel="next"] inside pagination (fallback .next a)
    count       [data-sp-count] anywhere in document (toolbar) — optional
*/
(function () {
  'use strict';

  /* ---- ONE const block (no magic numbers elsewhere) ---- */
  var DEFAULTS = {
    autoPages: 5,            /* overridden by data-sp-auto-pages (section setting) */
    rootMargin: '600px 0px', /* sentinel pre-trigger distance (bottom backstop) */
    prefetchRatio: 0.7,      /* scroll-progress prefetch: fire the next fetch once the
                                viewport bottom passes this fraction of the LOADED grid,
                                so a fast scroll can't outrun the network round-trip.
                                Overridden by data-sp-prefetch-ratio (section setting). */
    sectionId: 'sp-catalog-grid', /* fallback only — real id derived from wrapper */
    liteAutoPages: 1,        /* 3g profile */
    shortHeightSlack: 10     /* px */
  };
  var SEL = {
    root: ['#sp-catalog-grid', '[data-sp-catalog-grid]'],
    cards: ['[data-sp-cards]', '.sp-catalog-grid__cards', '.sp-card-grid', '.product-grid'],
    pagination: ['[data-sp-pagination]', '.sp-catalog-pagination', '.pagination'],
    next: ['a[rel="next"]', '.next a']
  };
  var HIDDEN_CLASS = 'spx-scroll-hidden';
  var LOADMORE_ID = 'spx-scroll-loadmore';
  var STYLE_ID = 'spx-scroll-style';

  /* required platform APIs — bail silently, server pagination remains usable */
  if (!window.fetch || !window.DOMParser || !window.URLSearchParams) { return; }

  var state = {
    root: null,
    sectionId: DEFAULTS.sectionId,
    cards: null,
    pagination: null,
    sentinel: null,
    observer: null,
    nextPage: null,
    autoLoaded: 0,
    autoPages: DEFAULTS.autoPages,
    prefetchRatio: DEFAULTS.prefetchRatio,
    scrollBound: false,
    inFlight: false
  };

  /* ---------- helpers ---------- */

  function pick(scope, sels) {
    for (var i = 0; i < sels.length; i++) {
      var el = scope.querySelector(sels[i]);
      if (el) { return el; }
    }
    return null;
  }

  function fire(el, name, detail) {
    var ev;
    try {
      ev = new CustomEvent(name, { bubbles: true, cancelable: false, detail: detail });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail);
    }
    el.dispatchEvent(ev);
  }

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) { return; }
    var css = '' +
      '.' + HIDDEN_CLASS + '{display:none!important}' +
      '#' + LOADMORE_ID + '{display:flex;justify-content:center;padding:24px 0 48px}' +
      '#' + LOADMORE_ID + ' button{background:var(--sp-navy,#0A1D2E);color:#fff;border:0;' +
        'padding:14px 36px;font-family:var(--font-stack-body,inherit);font-size:11px;' +
        'font-weight:700;letter-spacing:2px;text-transform:uppercase;cursor:pointer;' +
        'border-radius:2px;transition:opacity .2s}' +
      '#' + LOADMORE_ID + ' button:hover{opacity:.85}' +
      '#' + LOADMORE_ID + ' button[disabled]{opacity:.6;cursor:wait}' +
      '.spx-scroll-spinner{display:inline-block;width:14px;height:14px;margin-right:10px;' +
        'border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;' +
        'animation:spx-scroll-spin .8s linear infinite;vertical-align:-2px}' +
      '@keyframes spx-scroll-spin{to{transform:rotate(360deg)}}';
    var st = document.createElement('style');
    st.id = STYLE_ID;
    st.textContent = css;
    document.head.appendChild(st);
  }

  /* saveData / effectiveType tiers (plan §3.3 + task brief) */
  function autoPageBudget() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (c) {
      if (c.saveData) { return 0; }
      var t = String(c.effectiveType || '');
      if (t === 'slow-2g' || t === '2g') { return 0; }
      if (t === '3g') { return DEFAULTS.liteAutoPages; }
    }
    var attr = state.root && state.root.getAttribute('data-sp-auto-pages');
    var n = attr ? parseInt(attr, 10) : NaN;
    return (isFinite(n) && n >= 0) ? n : DEFAULTS.autoPages;
  }

  /* real section id off the shopify-section wrapper (handles template--…__… ids) */
  function deriveSectionId(root) {
    var el = root;
    while (el && el.nodeType === 1) {
      var id = el.id || '';
      if (id.indexOf('shopify-section-') === 0) {
        return id.slice('shopify-section-'.length);
      }
      el = el.parentNode;
    }
    return DEFAULTS.sectionId; /* TODO-VERIFY: template JSON keys the grid section as "sp-catalog-grid" */
  }

  function parsePageParam(href) {
    if (!href) { return null; }
    var m = String(href).match(/[?&]page=(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  }

  function buildPageUrl(page) {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    if (page > 1) { params.set('page', String(page)); }
    var qs = params.toString();
    return window.location.pathname + '?' + (qs ? qs + '&' : '') +
      'section_id=' + encodeURIComponent(state.sectionId);
  }

  /* A ?page=N (N>1) URL — an old bookmark, a legacy deep link, or a browser Back that
     landed on a pre-fix polluted URL — makes Shopify render ONLY that page's slice, so
     earlier products are missing from the infinite grid. Reset to the clean page-1 URL
     (no history entry added) so the whole collection loads from the top. Returns true if
     it navigated away (caller should stop). */
  function deepPageReset() {
    var incoming = parsePageParam(window.location.search);
    if (!incoming || incoming <= 1) { return false; }
    try {
      var u = new URL(window.location.href);
      u.searchParams.delete('page');
      window.location.replace(u.pathname + u.search + u.hash);
      return true;
    } catch (e) { return false; }
  }

  /* ---------- total count (window._spCollectionTotal contract) ---------- */

  function extractTotal(doc, rawHtml) {
    var root = pick(doc, SEL.root);
    if (root && root.getAttribute('data-sp-total') !== null) {
      var n = parseInt(root.getAttribute('data-sp-total'), 10);
      if (isFinite(n)) { return n; }
    }
    var m = String(rawHtml).match(/_spCollectionTotal\s*=\s*(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  }

  function syncTotal(total) {
    if (total === null || !isFinite(total)) { return; }
    window._spCollectionTotal = total;
    /* patch the number inside the toolbar count, keep its wording */
    var el = document.querySelector('[data-sp-count]'); /* TODO-VERIFY toolbar hook */
    if (el && /[\d][\d,]*/.test(el.textContent)) {
      var formatted;
      try { formatted = total.toLocaleString(); } catch (e) { formatted = String(total); }
      el.textContent = el.textContent.replace(/[\d][\d,]*/, formatted);
    }
  }

  /* ---------- load-more button ---------- */

  function removeLoadMore() {
    var w = document.getElementById(LOADMORE_ID);
    if (w && w.parentNode) { w.parentNode.removeChild(w); }
  }

  function ensureLoadMore() {
    if (document.getElementById(LOADMORE_ID) || !state.pagination) { return; }
    var wrap = document.createElement('div');
    wrap.id = LOADMORE_ID;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'Load more';
    wrap.appendChild(btn);
    state.pagination.parentNode.insertBefore(wrap, state.pagination);
    btn.addEventListener('click', function () {
      state.autoLoaded = 0;
      removeLoadMore();
      doFetch();
    }, false);
  }

  function setBusy(busy) {
    var w = document.getElementById(LOADMORE_ID);
    if (!w) { return; }
    var btn = w.querySelector('button');
    if (!btn) { return; }
    if (busy) {
      btn.disabled = true;
      btn.innerHTML = '<span class="spx-scroll-spinner" aria-hidden="true"></span>Loading…';
    } else {
      btn.disabled = false;
      btn.textContent = 'Load more';
    }
  }

  /* ---------- fetch + append ---------- */

  function endOfCatalog() {
    state.nextPage = null;
    if (state.observer) { state.observer.disconnect(); }
    detachScroll();
    removeLoadMore();
    /* pagination stays hidden — every product is already on the page */
  }

  function degradeToPagination(err) {
    if (window.console && console.warn) { console.warn('[spx-catalog-scroll] fetch failed:', err); }
    if (state.observer) { state.observer.disconnect(); }
    detachScroll();
    removeLoadMore();
    /* pager is display:none by default now (FOUC fix) — force it visible on degrade */
    if (state.pagination) { state.pagination.classList.remove(HIDDEN_CLASS); state.pagination.style.display = 'flex'; }
    state.nextPage = null; /* stop; nothing cached — a re-init retries fresh */
  }

  function appendResponse(html, page) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newCards = pick(doc, SEL.cards);
    if (!newCards || !newCards.children.length) { endOfCatalog(); return; }

    /* appended content is below the fold — never eager/high priority */
    var imgs = newCards.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      imgs[i].removeAttribute('fetchpriority');
      imgs[i].setAttribute('loading', 'lazy');
    }

    var frag = document.createDocumentFragment();
    while (newCards.firstChild) { frag.appendChild(newCards.firstChild); }
    state.cards.appendChild(frag);
    state.autoLoaded++;

    syncTotal(extractTotal(doc, html));
    /* NOTE: intentionally do NOT write ?page=N to the URL — see the History-SAFE note in
       the header. The `page` arg is kept only for the sp-catalog:append event detail. */

    /* advance the cursor from the RESPONSE's pagination (source of truth) */
    var newPag = pick(doc, SEL.pagination);
    var newNext = newPag ? pick(newPag, SEL.next) : null;
    var nextPage = newNext ? parsePageParam(newNext.getAttribute('href')) : null;
    if (nextPage) {
      state.nextPage = nextPage;
      if (state.autoLoaded >= state.autoPages) {
        ensureLoadMore();
        if (state.observer) { state.observer.disconnect(); }
      } else if (state.sentinel) {
        /* After appending, a plain IntersectionObserver won't re-fire without an
           enter/leave transition — so the catalog stalls if the sentinel is still at/above
           the trigger line (short page, already at the bottom, or content BELOW the grid
           like the SEO block/footer let the scroll overshoot it). Re-check the sentinel's
           real position next frame: if it's within the trigger zone, chain to the next
           page; otherwise re-arm the observer to wait for the next scroll. */
        requestAnimationFrame(function () {
          if (!state.nextPage || state.inFlight) { return; }
          var rect = state.sentinel.getBoundingClientRect();
          if (rect.top <= (window.innerHeight || document.documentElement.clientHeight || 0) + 600) {
            doFetch();
          } else if (state.observer) {
            state.observer.unobserve(state.sentinel);
            state.observer.observe(state.sentinel);
          }
        });
      }
    } else {
      endOfCatalog();
    }

    /* hooks: RTS date fill on fresh cards + analytics/shortlist listeners */
    if (window.spxCatalogCards && window.spxCatalogCards.refresh) {
      window.spxCatalogCards.refresh(state.cards);
    }
    fire(state.root, 'sp-catalog:append', {
      page: page,
      total: window._spCollectionTotal
    });
  }

  function doFetch() {
    if (state.inFlight || !state.nextPage) { return; }
    state.inFlight = true;
    setBusy(true);
    var page = state.nextPage;
    fetch(buildPageUrl(page), {
      headers: { 'Accept': 'text/html' },
      credentials: 'same-origin'
    }).then(function (resp) {
      if (!resp.ok) { throw new Error('HTTP ' + resp.status); }
      return resp.text();
    }).then(function (html) {
      appendResponse(html, page);
      state.inFlight = false;
      setBusy(false);
    }).catch(function (err) {
      state.inFlight = false;
      setBusy(false);
      degradeToPagination(err);
    });
  }

  function onSentinel() {
    if (!state.nextPage || state.inFlight) { return; }
    if (state.autoLoaded >= state.autoPages) {
      ensureLoadMore();
      if (state.observer) { state.observer.disconnect(); }
      detachScroll();
      return;
    }
    doFetch();
  }

  /* ---------- scroll-progress prefetch ----------
     A passive, rAF-throttled scroll/resize listener (NOT a timer poller — it self-
     removes at end-of-catalog / budget handoff). Fires the next fetch the moment the
     viewport bottom passes `prefetchRatio` of the currently-loaded grid, keeping a
     rolling buffer of ~(1 - ratio) below the fold so a fast flick can't outrun it. */
  function scrollProgress() {
    if (!state.cards) { return -1; }
    var rect = state.cards.getBoundingClientRect();
    if (rect.height <= 0) { return -1; }
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    /* 0 = viewport bottom at grid top, 1 = viewport bottom at grid bottom, >1 = past it */
    return (vh - rect.top) / rect.height;
  }

  var scrollTick = false;
  function onScroll() {
    if (scrollTick) { return; }
    scrollTick = true;
    var raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
    raf(function () {
      scrollTick = false;
      if (!state.nextPage || state.inFlight) { return; }
      if (state.autoLoaded >= state.autoPages) { return; } /* Load-more owns the handoff */
      if (scrollProgress() >= state.prefetchRatio) { onSentinel(); }
    });
  }

  function attachScroll() {
    if (state.scrollBound) { return; }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    state.scrollBound = true;
  }

  function detachScroll() {
    if (!state.scrollBound) { return; }
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
    state.scrollBound = false;
  }

  /* ---------- idempotent init (window.spIfsInit contract) ---------- */

  function teardown() {
    if (state.observer) { state.observer.disconnect(); state.observer = null; }
    detachScroll();
    if (state.sentinel && state.sentinel.parentNode) {
      state.sentinel.parentNode.removeChild(state.sentinel);
    }
    state.sentinel = null;
    removeLoadMore();
    state.nextPage = null;
    state.autoLoaded = 0;
    state.inFlight = false;
  }

  function init() {
    teardown();

    state.root = pick(document, SEL.root);
    if (!state.root) { return; }
    state.sectionId = deriveSectionId(state.root);
    state.cards = pick(state.root, SEL.cards);
    state.pagination = pick(state.root, SEL.pagination);
    if (!state.cards || !state.pagination) { return; }

    var nextLink = pick(state.pagination, SEL.next);
    var nextPage = nextLink ? parsePageParam(nextLink.getAttribute('href')) : null;
    if (!nextPage) { return; } /* single/last page — leave server pagination alone */

    state.nextPage = nextPage;
    state.autoPages = autoPageBudget();

    var prAttr = state.root.getAttribute('data-sp-prefetch-ratio');
    var pr = prAttr ? parseFloat(prAttr) : NaN;
    state.prefetchRatio = (isFinite(pr) && pr > 0 && pr < 1) ? pr : DEFAULTS.prefetchRatio;

    ensureStyles();
    state.pagination.classList.add(HIDDEN_CLASS);

    state.sentinel = document.createElement('div');
    state.sentinel.id = 'spx-scroll-sentinel';
    state.sentinel.style.cssText = 'height:1px;width:100%;margin-top:-1px;pointer-events:none;';
    state.pagination.parentNode.insertBefore(state.sentinel, state.pagination);

    if (!('IntersectionObserver' in window) || state.autoPages === 0) {
      /* constrained connection or missing IO -> manual from the start */
      ensureLoadMore();
      return;
    }

    state.observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { onSentinel(); break; }
      }
    }, { rootMargin: DEFAULTS.rootMargin });
    state.observer.observe(state.sentinel);

    /* scroll-progress prefetch drives early loading; the observer above is the
       bottom backstop. Nudge once in case the first page is already short. */
    attachScroll();
    onScroll();
  }

  /* exported under the compat name spx-catalog.js (and legacy callers) invoke */
  window.spIfsInit = init;
  window.spxCatalogScroll = { init: init };

  /* self-heal after any engine swap (idempotent — double call with spIfsInit is safe) */
  document.addEventListener('collection:reloaded', init, false);

  /* first-load bootstrap: recover a stray ?page=N before wiring the grid. Section swaps
     (filter changes) call init() directly via spIfsInit / 'collection:reloaded' and never
     carry a stray page, so they bypass the reset. */
  function bootstrap() {
    if (deepPageReset()) { return; } /* navigating to the clean URL — let it reload */
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, false);
  } else {
    bootstrap();
  }
})();
